import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchClassWarmUpGroups } from "components/pages/WarmUpPicker/warmUpApi";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { logEducatorDashboard, LogLevel } from "utils";

// The class's warm-up assignments (GET /warm-up-groups/classrooms/{id}), read
// only when `enabled` (the flag is on). Cached so a return navigation renders
// at once, stale immediately so every mount revalidates, as quick groups do.
// `loading` is true only while the first read of a class is in flight. A
// failure reads as no warm-ups, with the error to show; `request` has already
// retried it, so the query does not retry again.
//
// While not enabled (flag off or unknown) nothing at all comes out: no list,
// no error and a refetch that does nothing, whatever an earlier identity left
// in the cache. The cache is also kept per identity (the impersonated teacher's
// id, as the flags are), so one teacher's list is never another's.
const warmUpGroupsKey = (identity, classroomId) => ["warm-up-groups", identity, classroomId];

const NO_WARM_UPS = [];
const doNothing = () => Promise.resolve();

const useIdentity = () => useImpersonation().impersonatedUser?.id || "self";

// Puts a saved assignment in place of its old copy in the cached list: same
// position, no re-sort and no new read (the server order applies next load).
const useReplaceWarmUpAssignment = (identity, classroomId) => {
  const queryClient = useQueryClient();
  return useCallback(
    (updated) =>
      queryClient.setQueryData(warmUpGroupsKey(identity, classroomId), (list) =>
        list
          ? {
              ...list,
              warmup_assignments: list.warmup_assignments.map((a) => (a.id === updated.id ? updated : a)),
            }
          : list
      ),
    [queryClient, identity, classroomId]
  );
};

export const useClassroomWarmUpGroups = (classroomId, enabled) => {
  const identity = useIdentity();
  const reads = Boolean(classroomId) && enabled;
  const { data, dataUpdatedAt, isInitialLoading, error, refetch } = useQuery({
    queryKey: warmUpGroupsKey(identity, classroomId),
    queryFn: async () => {
      try {
        return await fetchClassWarmUpGroups(classroomId);
      } catch (e) {
        logEducatorDashboard("useClassroomWarmUpGroups: failed to load warm-up groups", {
          level: LogLevel.WARNING,
          event: "warm_up_groups_list_error",
          statusCode: e?.status,
          classroomId,
        });
        throw e;
      }
    },
    enabled: reads,
    staleTime: 0,
    retry: false,
  });
  const replaceAssignment = useReplaceWarmUpAssignment(identity, classroomId);

  if (!reads) return { warmUpAssignments: NO_WARM_UPS, loading: false, error: null, refetch: doNothing, replaceAssignment, updatedAt: 0 };
  return {
    warmUpAssignments: (error ? null : data?.warmup_assignments) || [],
    loading: isInitialLoading,
    error: error || null,
    refetch,
    replaceAssignment,
    // When the cached list last changed (a read, or an assignment replaced in place).
    updatedAt: dataUpdatedAt,
  };
};

